import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Palette, Building2, FileText } from "lucide-react";

interface BrandingData {
  primaryColor: string;
  industry: string;
  description: string;
}

interface StepBrandingProps {
  onComplete: (data: BrandingData) => void;
  onSkip: () => void;
}

const INDUSTRIES = [
  { value: 'technology', label: 'Technologie & Software' },
  { value: 'finance', label: 'Finanzdienstleistungen' },
  { value: 'healthcare', label: 'Gesundheitswesen' },
  { value: 'manufacturing', label: 'Produktion & Fertigung' },
  { value: 'retail', label: 'Einzelhandel & E-Commerce' },
  { value: 'consulting', label: 'Beratung & Professional Services' },
  { value: 'education', label: 'Bildung & Training' },
  { value: 'real_estate', label: 'Immobilien' },
  { value: 'marketing', label: 'Marketing & Werbung' },
  { value: 'logistics', label: 'Logistik & Transport' },
  { value: 'hospitality', label: 'Gastgewerbe & Tourismus' },
  { value: 'energy', label: 'Energie & Umwelt' },
  { value: 'other', label: 'Andere' },
];

const PRESET_COLORS = [
  { value: '#3b82f6', label: 'Blau' },
  { value: '#8b5cf6', label: 'Lila' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#f59e0b', label: 'Orange' },
  { value: '#10b981', label: 'Grün' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#ef4444', label: 'Rot' },
  { value: '#6366f1', label: 'Indigo' },
];

export const StepBranding = ({ onComplete, onSkip }: StepBrandingProps) => {
  const [primaryColor, setPrimaryColor] = useState('#3b82f6');
  const [industry, setIndustry] = useState('');
  const [description, setDescription] = useState('');

  const handleComplete = () => {
    onComplete({
      primaryColor,
      industry,
      description: description.trim(),
    });
  };

  const canProceed = industry !== '';

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Workspace anpassen</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Gestalte deinen Workspace nach deinen Vorstellungen. Diese Einstellungen können später jederzeit angepasst werden.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Primärfarbe
            </CardTitle>
            <CardDescription>
              Wähle die Hauptfarbe für deinen Workspace
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setPrimaryColor(color.value)}
                  className={`h-12 rounded-lg transition-all hover:scale-110 ${
                    primaryColor === color.value 
                      ? 'ring-2 ring-offset-2 ring-foreground scale-110' 
                      : ''
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="custom-color" className="shrink-0">Eigene Farbe:</Label>
              <div className="flex gap-2 items-center flex-1">
                <Input
                  id="custom-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-16 h-10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#3b82f6"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="p-4 rounded-lg border" style={{ backgroundColor: primaryColor }}>
              <p className="text-white font-medium text-center">Vorschau</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Branche
            </CardTitle>
            <CardDescription>
              In welcher Branche ist dein Unternehmen tätig?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger>
                <SelectValue placeholder="Branche auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((ind) => (
                  <SelectItem key={ind.value} value={ind.value}>
                    {ind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Beschreibung (Optional)
            </CardTitle>
            <CardDescription>
              Eine kurze Beschreibung deines Workspace
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="z.B. Unser Team nutzt diesen Workspace für strategische Produktentscheidungen..."
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {description.length} / 500 Zeichen
            </p>
          </CardContent>
        </Card>

        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={onSkip}
            type="button"
          >
            Überspringen
          </Button>
          <Button
            onClick={handleComplete}
            disabled={!canProceed}
            type="button"
          >
            Weiter
          </Button>
        </div>
      </div>
    </div>
  );
};
